import { Chip, Box, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';                  
import CustomTable from '../CustomTable';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { formatDateBR, formatMoney } from '../../utils';

const TYPES = {
  REVENDA: 'Revenda',
  REDUTO_CAPITAL: 'Reduto Capital',
};

const STAGES = {
  APPROVED: 'Aprovado',
  PENDING: 'Pendente',
  REJECTED: 'Reprovado',
};


const getTipoStyles = (tipo) => {
  switch (tipo) {
    case 'REVENDA':
      return { bg: '#E7F0FE', color: '#3B82F6', icon: <StorefrontIcon sx={{ fontSize: 14 }} /> };
    case 'REDUTO_CAPITAL':
      return {
        bg: '#DCFCE7',
        color: '#22C55E',
        icon: <AccountBalanceIcon sx={{ fontSize: 14 }} />,
      };
    default:
      return { bg: '#F3F4F6', color: '#6B7280', icon: null };
  }
};

const getStatusStyles = (status) => {
  switch (status) {
    case 'APPROVED':
      return { bg: '#DCFCE7', color: '#16A34A' };
    case 'PENDING':
      return { bg: '#FEF9C3', color: '#CA8A04' };
    case 'REJECTED':
      return { bg: '#FDE8E8', color: '#DC2626' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

const SimulationsTable = ({ simulations = [], isSecondDesign = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const baseColumns = [
    ...(!isSecondDesign
      ? [
          {
            field: 'client_name',
            headerName: t('simulation.client'),
            render: (value, row) => (
              <Box>
                <Typography variant="body2" sx={{ color: '#2C2C2C', fontWeight: 600, mb: 0.5, fontSize: '14px' }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#737373', fontSize: '12px' }}>
                  {row?.company?.name}
                </Typography>
              </Box>
            ),
          },
        ]
      : []),

    {
      field: 'type',
      headerName: t('simulation.type'),
      render: (value) => {
        const tipoStyles = getTipoStyles(value);
        return (
          <Chip
            icon={tipoStyles.icon}
            label={TYPES[value]}
            size="small"
            sx={{
              bgcolor: tipoStyles.bg,
              color: tipoStyles.color,
              fontWeight: 500,
              paddingY: '4px',
              paddingX: '8px',
              fontSize: '12px',
              '& .MuiChip-label': { px: 1 },
              '& .MuiChip-icon': { color: tipoStyles.color, ml: 0.5 },
            }}
          />
        );
      },
    },

    {
      field: 'total_value',
      headerName: t('simulation.value'),
      render: (value) => (
        <Typography sx={{ color: '#0F1729', fontWeight: 600, fontSize: '14px' }}>
          {formatMoney(value)}
        </Typography>
      ),
    },

    {
      field: 'installments',
      headerName: t('simulation.installments'),
      render: (value) => `${value}x`,
    },

    {
      field: 'stage',
      headerName: 'Status',
      render: (value) => {
        const statusStyles = getStatusStyles(value);
        return (
          <Chip
            label={STAGES[value]}
            size="small"
            sx={{
              bgcolor: statusStyles.bg,
              color: statusStyles.color,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        );
      },
    },

    {
      field: 'created_at',
      headerName: t('simulation.date'),
      render: (value) => (
        <Typography sx={{ color: '#65758B', fontSize: '14px' }}>
          {formatDateBR(value)}
        </Typography>
      ),
    },
  ];

  const actions = isSecondDesign
    ? [
        {
          icon: <Edit sx={{ fontSize: 16 }} />,
          tooltip: t('simulation.actions'),
          onClick: (row) => navigate(`/app/simulation/${row?.id}`),
        },
      ]
    : [];

  return (
    <CustomTable
      columns={baseColumns}
      rows={simulations}
      rowKey="id"
      actions={actions}
      emptyMessage={t('simulation.noSimulationsFound')}
    />
  );
};

export default SimulationsTable;